'use client'

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot, $isTextNode, $isParagraphNode } from 'lexical'
import { $isMentionNode } from './MentionNode'
import { $getContextForParagraph } from './editorContext'

const STYLE_ATTR = 'data-mention-style'
const PARAGRAPH_STYLE_ATTR = 'data-paragraph-context'
const INDENT_ATTR = 'data-paragraph-indent'

// Indentation applied to script body relative to the segment/scene headings.
// Each level stacks on top of the previous, so paragraphs inside a scene that
// is itself inside a segment get SEGMENT_INDENT_PX + SCENE_INDENT_PX.
const SEGMENT_INDENT_PX =0
const SCENE_INDENT_PX = 0

function applyDomStyles(editor) {
    const rootElement = editor.getRootElement()
    if (!rootElement) return

    rootElement.querySelectorAll(`[${STYLE_ATTR}]`).forEach(el => {
        el.removeAttribute(STYLE_ATTR)
        el.style.color = ''
        el.style.fontStyle = ''
        el.style.fontWeight = ''
        el.style.fontSize = ''
    })

    rootElement.querySelectorAll(`[${PARAGRAPH_STYLE_ATTR}]`).forEach(el => {
        el.removeAttribute(PARAGRAPH_STYLE_ATTR)
        el.style.textAlign = ''
        el.style.maxWidth = ''
        el.style.margin = ''
    })

    rootElement.querySelectorAll(`[${INDENT_ATTR}]`).forEach(el => {
        el.removeAttribute(INDENT_ATTR)
        el.style.paddingLeft = ''
    })

    editor.getEditorState().read(() => {
        const root = $getRoot()
        const paragraphs = root.getChildren().filter($isParagraphNode)

        let segmentSeen = false
        let sceneSeen = false

        paragraphs.forEach((paragraph, pIndex) => {
            const children = paragraph.getChildren()

            let isSegmentHeading = false
            let isSceneHeading = false
            for (const child of children) {
                if (!$isMentionNode(child)) continue
                if (child.getMentionType() !== 'command') continue
                const id = child.getMentionId()
                if (id === 'segment') isSegmentHeading = true
                else if (id === 'scene') isSceneHeading = true
            }

            let depth
            if (isSegmentHeading) {
                depth = 0
                segmentSeen = true
                sceneSeen = false
            } else if (isSceneHeading) {
                depth = segmentSeen ? 1 : 0
                sceneSeen = true
            } else {
                depth = (segmentSeen ? 1 : 0) + (sceneSeen ? 1 : 0)
            }

            const indentPx = (depth >= 1 ? SEGMENT_INDENT_PX : 0) + (depth >= 2 ? SCENE_INDENT_PX : 0)
            const paragraphDom = editor.getElementByKey(paragraph.getKey())
            if (paragraphDom && indentPx > 0) {
                paragraphDom.setAttribute(INDENT_ATTR, String(depth))
                paragraphDom.style.paddingLeft = `${indentPx}px`
            }

            const context = $getContextForParagraph(paragraph, paragraphs, pIndex)
            if (!context) return

            if (paragraphDom && context === 'character') {
                paragraphDom.setAttribute(PARAGRAPH_STYLE_ATTR, context)
                paragraphDom.style.textAlign = 'center'
                paragraphDom.style.maxWidth = '65%'
                paragraphDom.style.margin = '0 auto'
            }

            const hasMentionInParagraph = children.some(c => $isMentionNode(c))
            let afterMention = !hasMentionInParagraph
            for (const child of children) {
                if ($isMentionNode(child)) {
                    afterMention = true
                    continue
                }

                if (!$isTextNode(child) || (!afterMention && context !== 'character')) continue

                const key = child.getKey()
                const domElement = editor.getElementByKey(key)
                if (!domElement) continue

                domElement.setAttribute(STYLE_ATTR, context)

                switch (context) {
                    case 'segment':
                        domElement.style.color = '#E5FE04'
                        domElement.style.fontStyle = ''
                        domElement.style.fontWeight = 'bold'
                        domElement.style.fontSize = '1.2em'
                        break
                    case 'scene':
                        domElement.style.color = '#c8c8c8'
                        domElement.style.fontStyle = ''
                        domElement.style.fontWeight = 'normal'
                        domElement.style.fontSize = '1.05em'
                        break
                    case 'description':
                        domElement.style.color = '#9ca3af'
                        domElement.style.fontStyle = ''
                        break
                    case 'character':
                        domElement.style.color = ''
                        domElement.style.color = '#c8c8c8'
                        domElement.style.fontStyle = 'italic'

                        break
                }
            }
        })
    })
}

export default function TextStylingPlugin() {
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        return editor.registerUpdateListener(() => {
            requestAnimationFrame(() => {
                applyDomStyles(editor)
            })
        })
    }, [editor])

    return null
}
